import React from "react";
import { designStore, generalConfigStore, setProject } from "@store";
import { useSnapshot } from "valtio";
import { t } from "i18next";
import ReactSwitch from "react-switch";

// Champs du formulaire d'inscription dont le switch est verrouillé (non
// désactivable par l'utilisateur). Extrait en constante nommée : si la clé
// change côté modèle de données, la condition reste détectable par recherche.
// ESS-3883 : seul `phoneNumber` reste verrouillé. `firstName`, auparavant
// verrouillé lui aussi, est désormais activable/désactivable par l'utilisateur
// (ouverture volontaire de l'édition de ce champ).
const DISABLED_REGISTER_FIELDS: readonly string[] = ["phoneNumber"];

// Groupe de champs masqués du formulaire d'inscription (non rendus dans la
// liste de switches). Extrait en constante nommée plutôt qu'un littéral en
// condition.
const EXCLUDED_REGISTER_FORM_GROUP = 4;

export function SwitchItemsComponent(): JSX.Element {
    const { project } = useSnapshot(generalConfigStore);
    const { registerForm } = useSnapshot(designStore);

    // Le type de `subSteps` est trop permissif (index signature) : on resserre
    // une seule fois ici vers la forme réellement consommée, plutôt que de
    // répéter `as PageType` / `as registerFormType` à chaque accès.
    const accountFidelite = project.template.pages.subSteps[
        "accountFidelite"
    ] as PageType;
    const accountFideliteForm = accountFidelite.registerForm as registerFormType;

    const isActiveSwitch = (elementName: string) => {
        return accountFideliteForm[elementName]?.active ?? false;
    };

    function handleSwitchForm(
        checked: boolean,
        _: React.SyntheticEvent<MouseEvent | KeyboardEvent> | MouseEvent,
        id: string
    ): void {
        // On lit le store LIVE (proxy Valtio) — et non le snapshot de rendu —
        // pour bâtir le patch : deux toggles rapprochés (avant un re-render)
        // partiraient sinon du même snapshot gelé, le 2e écrasant le 1er. Lire
        // l'état courant à l'appel garantit que chaque mutation s'empile.
        const liveProject = generalConfigStore.project;
        const liveAccountFidelite = liveProject.template.pages.subSteps[
            "accountFidelite"
        ] as PageType;
        const liveForm = liveAccountFidelite.registerForm as registerFormType;

        setProject({
            ...liveProject,
            template: {
                ...liveProject.template,
                pages: {
                    ...liveProject.template.pages,
                    subSteps: {
                        ...liveProject.template.pages.subSteps,
                        accountFidelite: {
                            ...liveAccountFidelite,
                            registerForm: {
                                ...liveForm,
                                [id]: {
                                    ...liveForm[id],
                                    active: checked,
                                },
                            },
                        },
                    },
                },
            },
        });
    }

    const registerFormNewOrder = Object.fromEntries(
        Object.entries(registerForm)
            .sort((a, b) => a[1].group - b[1].group)
            .filter(
                ([_, element]) => element.group !== EXCLUDED_REGISTER_FORM_GROUP
            )
    );
    return (
        <React.Fragment>
            {Object.keys(registerFormNewOrder).map((element: string) => {
                return (
                    <div
                        key={element}
                        className="d-flex justify-content-between align-items-center mb-3"
                    >
                        <label htmlFor={element}>{t(element)}</label>
                        {/* data-testid posé sur un wrapper que l'on contrôle
                                plutôt que sur ReactSwitch : react-switch ne
                                garantit pas le forwarding des props non standard
                                vers l'<input> DOM, donc le testid resterait
                                fiable même si la lib changeait. */}
                        <span
                            data-testid={`connection-design-switch-${element}`}
                        >
                            <ReactSwitch
                                uncheckedIcon={false}
                                checkedIcon={false}
                                handleDiameter={26}
                                offColor="#f7b4b8"
                                offHandleColor="#E30613"
                                id={element}
                                checked={isActiveSwitch(element)}
                                onChange={handleSwitchForm}
                                onColor="#c2eddd"
                                onHandleColor="#34C38F"
                                width={50}
                                height={20}
                                disabled={DISABLED_REGISTER_FIELDS.includes(
                                    element
                                )}
                            />
                        </span>
                    </div>
                );
            })}
        </React.Fragment>
    );
}
